import React from 'react';
import { Filter, FilterType } from '@dfl/mui-admin-layout';
import internationalAddressServices from 'lib/dfl-address-form-mui/components/AddressMapFormFields/services/international-address.services';
import { AddressService } from 'lib/dfl-address-form-mui/services';
import StateAddressFilter from 'lib/dfl-address-filter/components/StateAddressFilter';
import CityAddressFilter from 'lib/dfl-address-filter/components/CityAddressFilter';
import CountryAddressFilter from 'lib/dfl-address-filter/components/CountryAddressFilter';
import { isArray } from 'lodash';
import { OperatorFilter, TermFilter } from '@dofleini/query-builder';

const buildAddressTermTransform = (field: string) => (value: any) => {
  const isArr = value?.split(',');
  if (isArray(isArr)) {
    return new OperatorFilter({
      type: 'OR',
      filters: isArr?.map((item: any) => ({
        type: 'TERM',
        field,
        value: item,
        objectId: false,
        isDate: false,
      })),
    })?.toQuery();
  }
  return new TermFilter({ field, value, objectId: false }).toQuery();
};

export const defaultAddressFilters: Filter[] = [
  // Country filter
  {
    filter: 'common:country',
    translate: true,
    type: FilterType.DYNAMIC_LIST,
    key: 'country',
    field: 'shipping.address.country',
    fetchFunc: internationalAddressServices.searchCountries,
    mapEntities: (rows: any[]) => rows?.map((row: any) => ({ ...row, label: row?.name, value: String(row?.code) })),
    multiple: false,
  },

  // State filter
  {
    filter: 'common:fields.address.state',
    translate: true,
    type: FilterType.DYNAMIC_LIST,
    key: 'state',
    field: 'shipping.address.state',
    // @ts-ignore
    dependsOn: ['country'],
    fetchFunc: async (params: any) => {
      const country = params?.country;

      if (country === 'CU') {
        // Cuba: provinces
        return await AddressService.searchState(params);
      } else if (country) {
        // Internacional: states
        return await internationalAddressServices.searchStates({ country });
      }

      return [];
    },
    mapEntities: (rows: any[]) => rows?.map((row: any) => ({ ...row, label: row?.name, value: String(row?.code) })),
    multiple: false,
    Component: StateAddressFilter,
    transform: buildAddressTermTransform('shipping.address.state'),
  },

  // City filter (Only for Cuba)
  {
    filter: 'common:municipalities',
    translate: true,
    type: FilterType.DYNAMIC_LIST,
    key: 'city',
    field: 'shipping.address.city',
    // @ts-ignore
    dependsOn: ['country', 'state'],
    fetchFunc: async (params: any) => {
      const country = params?.country;
      const stateCode = params?.state;

      if (country !== 'CU' && !stateCode) {
        return null;
      }

      const states = String(stateCode)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      const responses = await Promise.all(states.map((code) => AddressService.searchCity(code, params?.search)));

      return {
        total: responses.reduce((acc, response) => acc + (response?.total || 0), 0),
        page: 1,
        data: responses.flatMap((response) => response?.data || []),
      };
    },
    mapEntities: (rows: any[]) => rows?.map((row: any) => ({ ...row, label: row?.name, value: String(row?.code) })),
    multiple: false,
    Component: CityAddressFilter,
    transform: buildAddressTermTransform('shipping.address.city'),
  },
];

export type AddressFiltersProps = {
  countryFilter: string;
  countryKey: string;
  countryField: string;
  stateFilter: string;
  stateKey: string;
  stateField: string;
  cityFilter: string;
  cityKey: string;
  cityField: string;
  justUseFor?: string;
  hideCity?: boolean;
};

export const addressFilters = ({
  countryFilter,
  countryKey,
  countryField,
  stateFilter,
  stateKey,
  stateField,
  cityFilter,
  cityKey,
  cityField,
  justUseFor,
  hideCity,
}: AddressFiltersProps): Filter[] => {
  const filters = defaultAddressFilters?.map((filter, index) => {
    const newFilter: Filter = { ...filter };

    // Country filter (index 0)
    if (index === 0) {
      if (countryFilter) newFilter.filter = countryFilter;
      if (countryKey) newFilter.key = countryKey;
      if (countryField) newFilter.field = countryField;

      // Create wrapper component for CountryAddressFilter
      const CountryFilterWrapper = React.memo((props: any) => (
        <CountryAddressFilter {...props} stateKey={stateKey} cityKey={cityKey} justUseFor={justUseFor} />
      ));
      CountryFilterWrapper.displayName = 'CountryFilterWrapper';
      newFilter.Component = CountryFilterWrapper;
    }

    // State filter (index 1)
    if (index === 1) {
      if (stateFilter) newFilter.filter = stateFilter;
      if (stateKey) newFilter.key = stateKey;
      if (stateField) {
        newFilter.field = stateField;
        newFilter.transform = buildAddressTermTransform(stateField);
      }

      // Create wrapper component for StateAddressFilter
      const StateFilterWrapper = React.memo((props: any) => (
        <StateAddressFilter {...props} cityKey={cityKey} justUseFor={justUseFor} multiple />
      ));
      StateFilterWrapper.displayName = 'StateFilterWrapper';
      newFilter.Component = StateFilterWrapper;
    }

    // City filter (index 2)
    if (index === 2) {
      if (cityFilter) newFilter.filter = cityFilter;
      if (cityKey) newFilter.key = cityKey;
      if (cityField) {
        newFilter.field = cityField;
        newFilter.transform = buildAddressTermTransform(cityField);
      }

      // Create wrapper component for CityAddressFilter
      const CityFilterWrapper = React.memo((props: any) => (
        <CityAddressFilter
          {...props}
          countryKey={countryKey}
          stateKey={stateKey}
          justUseFor={justUseFor}
          hideCity={hideCity}
          multiple
        />
      ));
      CityFilterWrapper.displayName = 'CityFilterWrapper';
      newFilter.Component = CityFilterWrapper;
    }

    return newFilter;
  });

  // Filter out city filter if hideCity is true
  return hideCity ? filters.filter((_, index) => index !== 2) : filters;
};
